import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, FileSignature, Search, Trash2, Mail, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Dialog, DialogContent, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../components/ui/command';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_META = {
    draft:                { label: 'Draft',                color: 'bg-slate-100  text-slate-700  border-slate-300'  },
    issued:               { label: 'Issued',               color: 'bg-sky-50     text-sky-700    border-sky-200'    },
    viewed:               { label: 'Viewed',               color: 'bg-amber-50   text-amber-700  border-amber-200'  },
    signed_by_recipient:  { label: 'Awaiting counter-sign',color: 'bg-violet-50  text-violet-700 border-violet-200' },
    signed:               { label: 'Signed',               color: 'bg-emerald-50 text-emerald-700 border-emerald-200'},
    expired:              { label: 'Expired',              color: 'bg-rose-50    text-rose-700   border-rose-200'   },
    cancelled:            { label: 'Cancelled',            color: 'bg-slate-50   text-slate-500  border-slate-200'  },
};

export function StatusChip({ status }) {
    const meta = STATUS_META[status] || STATUS_META.draft;
    return (
        <span className={`inline-flex items-center text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${meta.color}`}>
            {meta.label}
        </span>
    );
}

export default function NDAsList() {
    const { can } = useAuth();
    const canCreate = can('proposals', 'create');
    const canDelete = can('proposals', 'delete');
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [rows, setRows] = useState(null);
    const [query, setQuery] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [createDefaults, setCreateDefaults] = useState({});

    // If the user landed here from a client page via ?client_id=…, open the
    // create modal with that client pre-selected.
    useEffect(() => {
        const cid = params.get('client_id');
        if (cid) {
            setCreateDefaults({ client_id: cid });
            setShowCreate(true);
            // Clear the param so refresh / back doesn't re-open it
            params.delete('client_id');
            setParams(params, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(async () => {
        try {
            const r = await api.get('/ndas');
            setRows(r.data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to load NDAs');
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (!rows) return [];
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r =>
            (r.recipient_name || '').toLowerCase().includes(q)
            || (r.recipient_company || '').toLowerCase().includes(q)
            || (r.recipient_email || '').toLowerCase().includes(q)
            || (r.nda_number || '').toLowerCase().includes(q)
            || (r.client_name || '').toLowerCase().includes(q)
        );
    }, [rows, query]);

    const remove = async (row) => {
        if (!window.confirm(`Delete NDA ${row.nda_number}? (signed NDAs cannot be deleted)`)) return;
        try {
            await api.delete(`/ndas/${row.id}`);
            toast.success('Deleted');
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Delete failed');
        }
    };

    return (
        <div data-testid="ndas-list">
            <PageHeader
                title="NDAs"
                description="Mutual Non-Disclosure Agreements — issue, track signatures, store signed PDFs against clients."
                actions={
                    canCreate && (
                        <Button onClick={() => setShowCreate(true)} data-testid="new-nda-btn">
                            <Plus className="w-4 h-4 mr-1" /> New NDA
                        </Button>
                    )
                }
            />
            <div className="px-8 py-4 border-b border-[color:var(--bm-border)] bg-white">
                <div className="relative max-w-md">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by recipient, company, email, NDA number…"
                        className="pl-8 h-9"
                        data-testid="ndas-search"
                    />
                </div>
            </div>

            <div className="px-8 py-6">
                {!rows && <div className="text-sm text-slate-500">Loading…</div>}
                {rows && filtered.length === 0 && (
                    <div className="text-center py-16 text-slate-500">
                        <FileSignature className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <div className="text-sm">No NDAs yet.</div>
                        {canCreate && (
                            <Button variant="outline" className="mt-3" onClick={() => setShowCreate(true)}>
                                <Plus className="w-4 h-4 mr-1" /> Create the first one
                            </Button>
                        )}
                    </div>
                )}
                {filtered.length > 0 && (
                    <div className="bg-white border border-[color:var(--bm-border)] rounded-lg overflow-hidden">
                        <table className="w-full text-[13px]">
                            <thead className="bg-slate-50 border-b border-[color:var(--bm-border)]">
                                <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                                    <th className="text-left font-semibold px-3 py-2.5">NDA #</th>
                                    <th className="text-left font-semibold px-3 py-2.5">Recipient</th>
                                    <th className="text-left font-semibold px-3 py-2.5">Company</th>
                                    <th className="text-left font-semibold px-3 py-2.5">Client</th>
                                    <th className="text-left font-semibold px-3 py-2.5">Status</th>
                                    <th className="text-left font-semibold px-3 py-2.5">Updated</th>
                                    <th className="px-3 py-2.5 w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(r => (
                                    <tr
                                        key={r.id}
                                        className="border-b border-[color:var(--bm-border)] hover:bg-slate-50 cursor-pointer"
                                        onClick={() => navigate(`/ndas/${r.id}`)}
                                        data-testid={`nda-row-${r.id}`}
                                    >
                                        <td className="px-3 py-2.5 font-medium text-slate-800">{r.nda_number}</td>
                                        <td className="px-3 py-2.5">{r.recipient_name}<div className="text-[11px] text-slate-500">{r.recipient_email}</div></td>
                                        <td className="px-3 py-2.5 text-slate-700">{r.recipient_company || '—'}</td>
                                        <td className="px-3 py-2.5 text-slate-700">{r.client_name || '—'}</td>
                                        <td className="px-3 py-2.5"><StatusChip status={r.status} /></td>
                                        <td className="px-3 py-2.5 text-slate-500 text-[12px]">{fmtDate(r.updated_at)}</td>
                                        <td className="px-3 py-2.5 text-right">
                                            {canDelete && r.status !== 'signed' && (
                                                <button onClick={(e) => { e.stopPropagation(); remove(r); }} className="p-1.5 text-slate-400 hover:text-rose-600" title="Delete">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showCreate && (
                <CreateNDAModal
                    defaults={createDefaults}
                    onClose={() => { setShowCreate(false); setCreateDefaults({}); }}
                    onCreated={(nda) => { setShowCreate(false); navigate(`/ndas/${nda.id}`); }}
                />
            )}
        </div>
    );
}


function CreateNDAModal({ onClose, onCreated, defaults }) {
    const [clients, setClients] = useState([]);
    const [form, setForm] = useState({
        recipient_name: '',
        recipient_company: '',
        recipient_email: '',
        recipient_address: '',
        purpose: '',
        client_project_name: '',
        duration: '3 months',
        confidentiality_period: 'five (5) years',
        special_terms: '',
        client_id: defaults?.client_id || '',
        effective_date: new Date().toISOString().slice(0, 10),
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        // NDAs only apply once a client is out of "Lead" (discovery or further) —
        // exclude leads server-side, and fetch the full set (not just page 1) so
        // the dropdown isn't silently truncated.
        api.get('/clients', { params: { exclude_status: 'lead', per_page: 200 } }).then((r) => {
            // /api/clients returns { items: [...], total, page, ... } OR a plain list
            const list = Array.isArray(r.data)
                ? r.data
                : (r.data?.items || r.data?.clients || []);
            setClients(list.filter(c => !c.deleted_at));
        }).catch(() => {});
    }, []);

    const set = (k, v) => {
        setForm(f => {
            const next = { ...f, [k]: v };
            // When linking to a client, prefill company + project name if blank
            if (k === 'client_id' && v) {
                const c = clients.find(x => (x.id || x._id) === v);
                if (c) {
                    if (!f.recipient_company) next.recipient_company  = c.trading_name || c.company_name || '';
                    if (!f.client_project_name) next.client_project_name = c.trading_name || c.company_name || '';
                }
            }
            return next;
        });
    };

    const submit = async () => {
        if (!form.recipient_name.trim()) return toast.error('Recipient name is required');
        if (!form.recipient_email.trim()) return toast.error('Recipient email is required');
        setBusy(true);
        try {
            const payload = { ...form, client_id: form.client_id || null };
            const r = await api.post('/ndas', payload);
            toast.success('NDA created');
            onCreated(r.data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Create failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl">
                <DialogTitle>New NDA</DialogTitle>
                <DialogDescription className="sr-only">Create a new Non-Disclosure Agreement from the master template.</DialogDescription>
                <div className="grid grid-cols-2 gap-3 mt-2">
                    <Field label="Recipient name *">
                        <Input value={form.recipient_name} onChange={(e) => set('recipient_name', e.target.value)} data-testid="nda-recipient-name" />
                    </Field>
                    <Field label="Recipient email *">
                        <Input type="email" value={form.recipient_email} onChange={(e) => set('recipient_email', e.target.value)} data-testid="nda-recipient-email" />
                    </Field>
                    <Field label="Recipient company">
                        <Input value={form.recipient_company} onChange={(e) => set('recipient_company', e.target.value)} placeholder="(blank = recipient fills in)" />
                    </Field>
                    <Field label="Recipient address">
                        <Input value={form.recipient_address} onChange={(e) => set('recipient_address', e.target.value)} placeholder="(blank = recipient fills in)" />
                    </Field>
                    <Field label="Link to client">
                        <ClientCombobox
                            clients={clients}
                            value={form.client_id}
                            onChange={(v) => set('client_id', v)}
                        />
                    </Field>
                    <Field label="Effective date">
                        <Input type="date" value={form.effective_date} onChange={(e) => set('effective_date', e.target.value)} />
                    </Field>
                    <Field label="Client / project name" wide>
                        <Input value={form.client_project_name} onChange={(e) => set('client_project_name', e.target.value)} placeholder="e.g. Atlas Build workspace" />
                    </Field>
                    <Field label="Purpose of access" wide>
                        <textarea
                            value={form.purpose}
                            onChange={(e) => set('purpose', e.target.value)}
                            rows={2}
                            className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
                            placeholder="e.g. supporting consulting delivery on Atlas Build's process maps and demos"
                            data-testid="nda-purpose"
                        />
                    </Field>
                    <Field label="Engagement duration">
                        <Input value={form.duration} onChange={(e) => set('duration', e.target.value)} />
                    </Field>
                    <Field label="Confidentiality period">
                        <Input value={form.confidentiality_period} onChange={(e) => set('confidentiality_period', e.target.value)} />
                    </Field>
                    <Field label="Special terms (optional)" wide>
                        <textarea value={form.special_terms} onChange={(e) => set('special_terms', e.target.value)} rows={2} className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
                    </Field>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} data-testid="nda-create-submit">
                        {busy ? 'Creating…' : 'Create NDA'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function ClientCombobox({ clients, value, onChange }) {
    const [open, setOpen] = useState(false);
    const selected = clients.find(c => (c.id || c._id) === value);
    const label = selected ? (selected.trading_name || selected.company_name) : '— optional —';
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm bg-white flex items-center justify-between"
                    data-testid="nda-client-select"
                >
                    <span className={cn('truncate', !selected && 'text-slate-400')}>{label}</span>
                    <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50 ml-1" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                    <CommandInput placeholder="Search clients…" data-testid="nda-client-search" />
                    <CommandList>
                        <CommandEmpty>No clients found.</CommandEmpty>
                        <CommandGroup>
                            <CommandItem
                                value="__none__"
                                onSelect={() => { onChange(''); setOpen(false); }}
                                data-testid="nda-client-option-none"
                            >
                                <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                                — optional —
                            </CommandItem>
                            {clients.map(c => {
                                const id = c.id || c._id;
                                const name = c.trading_name || c.company_name;
                                return (
                                    <CommandItem
                                        key={id}
                                        value={`${name} ${id}`}
                                        onSelect={() => { onChange(id); setOpen(false); }}
                                        data-testid={`nda-client-option-${id}`}
                                    >
                                        <Check className={cn('mr-2 h-4 w-4', value === id ? 'opacity-100' : 'opacity-0')} />
                                        {name}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}


function Field({ label, children, wide }) {
    return (
        <div className={wide ? 'col-span-2' : ''}>
            <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}


function fmtDate(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return iso; }
}
